import React, { useEffect, useState } from "react";
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRefresher,
  IonRefresherContent,
  IonBadge,
  IonSkeletonText,
  IonNote,
  RefresherEventDetail,
  useIonRouter,
  IonCardContent,
  IonCardHeader,
} from "@ionic/react";
import {
  arrowBackOutline,
  refreshOutline,
  checkmarkOutline,
  timeOutline,
  personAddOutline,
  notificationsOutline,
} from "ionicons/icons";
import { motion } from "framer-motion";
import { useAuth } from "../../hooks/useAuth";
import { useHaptics } from "../../hooks/useHaptics";
import {
  useNotifications,
  useSocketConnection,
} from "../../contexts/SocketContext";
import {
  acceptFollowRequest,
  pendingRequestsActionRequired,
  rejectFollowRequest,
} from "../../services/datashare";
import { PendingRequestListItem } from "../settings/PendingRequests";
import Header from "../../components/Header";
// ⛔ KERNEL-backed, unlike everything else on this screen (blaze socket.io). Kept as its
// own block rather than merged into the list — see the component header for why.
import CareTeamInbox from "../../components/engagement/CareTeamInbox";
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring" as const, stiffness: 100, damping: 15 },
  },
};

const NotificationsPage: React.FC = () => {
  const router = useIonRouter();
  const { user } = useAuth();
  const { successActivity, errorFeedback } = useHaptics();
  const { notifications, unreadCount, markAsRead, requestNotifications } =
    useNotifications();
  const { currentUser } = useSocketConnection();

  const [actionsRequiredItems, setActionsRequiredItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch notifications on component mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      const response = await pendingRequestsActionRequired();
      if (response && response.length > 0) {
        setActionsRequiredItems(response);
      }
      requestNotifications();
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle pull-to-refresh
  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    setIsRefreshing(true);
    try {
      await fetchNotifications();
      successActivity();
    } catch (error) {
      console.error("Error refreshing notifications:", error);
      errorFeedback();
    } finally {
      setIsRefreshing(false);
      event.detail.complete();
    }
  };

  // Accept follow request
  const handleAcceptRequest = async (selectedRequest: any): Promise<void> => {
    try {
      if (selectedRequest.type === "incoming_follower_request") {
        await acceptFollowRequest({ relationship_id: selectedRequest.id });
      } else if (selectedRequest.type === "incoming_following_request") {
        await acceptFollowRequest({ relationship_id: selectedRequest.id });
      }

      // Remove from local state
      setActionsRequiredItems((prev) =>
        prev.filter((item) => item.id !== selectedRequest.id)
      );

      successActivity();
      await fetchNotifications(); // Refresh to get updated state
    } catch (error) {
      console.error("Error accepting request:", error);
      errorFeedback();
    }
  };

  // Reject follow request
  const handleRejectRequest = async (selectedRequest: any): Promise<void> => {
    try {
      if (selectedRequest.type === "incoming_follower_request") {
        await rejectFollowRequest({ relationship_id: selectedRequest.id });
      } else if (selectedRequest.type === "incoming_following_request") {
        await rejectFollowRequest({ relationship_id: selectedRequest.id });
      }

      // Remove from local state
      setActionsRequiredItems((prev) =>
        prev.filter((item) => item.id !== selectedRequest.id)
      );

      successActivity();
      await fetchNotifications(); // Refresh to get updated state
    } catch (error) {
      console.error("Error rejecting request:", error);
      errorFeedback();
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "incoming_follower_request":
      case "incoming_following_request":
        return personAddOutline;
      default:
        return notificationsOutline;
    }
  };

  // Get notification color based on type

  // Handle notification item click
  const handleNotificationClick = (notification: any) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  return (
    <IonPage className="notifications-page">
      <Header
        pageTitle="Notifications"
        showBackButton={true}
        showAvatar={false}
        showUserIcon={false}
        showGreeting={false}
      />

      <IonCardContent className="bg-gray-50 min-h-[calc(100vh - 56px)] overflow-auto">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent
            pullingText="Pull to refresh notifications"
            refreshingSpinner="circles"
            refreshingText="Refreshing..."
          />
        </IonRefresher>

        <motion.div
          className="notifications-container px-2 py-2"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Care-team engagement (KERNEL) — first, because nothing else tells a patient a
              clinician replied: there is no push for it yet. */}
          <motion.div variants={itemVariants}>
            <CareTeamInbox />
          </motion.div>

          {/* Action Required Section */}
          {actionsRequiredItems.length > 0 && (
            <motion.div variants={itemVariants} className="mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-2 py-3 bg-amber-50 border-b border-amber-100">
                  <h2 className="text-lg font-semibold text-amber-800 flex items-center">
                    <IonIcon icon={timeOutline} className="mr-2" />
                    Action Required
                  </h2>
                  <p className="text-sm text-amber-700 mt-1">
                    These items need your attention
                  </p>
                </div>
                {actionsRequiredItems.map((request: any) => (
                  <div
                    key={request.id}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <PendingRequestListItem
                      request={request}
                      onAccept={handleAcceptRequest}
                      onReject={handleRejectRequest}
                      isLoading={isLoading}
                    />
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Regular Notifications Section */}
          {notifications.length > 0 && (
            <motion.div variants={itemVariants} className="mb-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-2 py-3 bg-blue-50 border-b border-blue-100">
                  <h2 className="text-lg font-semibold text-blue-800 flex items-center">
                    <IonIcon icon={notificationsOutline} className="mr-2" />
                    Recent Activity
                  </h2>
                </div>

                <IonList className="bg-transparent">
                  {notifications.map((notification: any) => (
                    <motion.div
                      key={notification.id}
                      variants={itemVariants}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <IonItem
                        button
                        onClick={() => handleNotificationClick(notification)}
                        className={`${
                          notification.isRead ? "bg-white" : "bg-blue-50"
                        } transition-colors duration-200`}
                      >
                        <div className="flex items-center w-full py-2">
                          <div className="flex-shrink-0 mr-3">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                notification.isRead
                                  ? "bg-gray-100"
                                  : "bg-blue-100"
                              }`}
                            >
                              <IonIcon
                                icon={getNotificationIcon(notification.type)}
                                className={
                                  notification.isRead
                                    ? "text-gray-600"
                                    : "text-blue-600"
                                }
                              />
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <IonLabel>
                              <h3
                                className={`font-medium ${
                                  notification.isRead
                                    ? "text-gray-700"
                                    : "text-gray-900"
                                }`}
                              >
                                {notification.title}
                              </h3>
                              <p
                                className={`text-sm mt-1 ${
                                  notification.isRead
                                    ? "text-gray-500"
                                    : "text-gray-600"
                                }`}
                              >
                                {notification.message}
                              </p>
                              <IonNote className="text-xs text-gray-500">
                                {new Date(
                                  notification.created_at
                                ).toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}{" "}
                                at{" "}
                                {new Date(
                                  notification.created_at
                                ).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </IonNote>
                            </IonLabel>
                          </div>

                          {!notification.is_seen && (
                            <div className="flex-shrink-0 ml-3">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            </div>
                          )}
                        </div>
                      </IonItem>
                    </motion.div>
                  ))}
                </IonList>
              </div>
            </motion.div>
          )}

          {/* Loading State */}
          {isLoading && (
            <motion.div variants={itemVariants} className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl px-2 shadow-sm">
                  <div className="flex items-center space-x-3">
                    <IonSkeletonText
                      animated
                      style={{
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                      }}
                    />
                    <div className="flex-1 space-y-2">
                      <IonSkeletonText animated style={{ width: "60%" }} />
                      <IonSkeletonText animated style={{ width: "80%" }} />
                      <IonSkeletonText animated style={{ width: "40%" }} />
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {/* Empty State */}
          {!isLoading &&
            notifications.length === 0 &&
            actionsRequiredItems.length === 0 && (
              <motion.div variants={itemVariants} className="text-center py-16">
                <div className="mx-auto w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <IonIcon
                    icon={notificationsOutline}
                    className="text-3xl text-gray-400"
                  />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No notifications
                </h3>
                <p className="text-gray-500 max-w-sm mx-auto">
                  You re all caught up! New notifications will appear here when
                  you receive them.
                </p>
              </motion.div>
            )}

          {/* Actions Required Empty State */}
          {!isLoading &&
            actionsRequiredItems.length === 0 &&
            notifications.length > 0 && (
              <motion.div variants={itemVariants} className="mb-6">
                <div className="bg-green-50 rounded-xl p-6 border border-green-100">
                  <div className="text-center">
                    <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                      <IonIcon
                        icon={checkmarkOutline}
                        className="text-xl text-green-600"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-green-800">
                      All caught up!
                    </h3>
                    <p className="text-xs text-green-700 mt-1">
                      No pending actions required
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
        </motion.div>
      </IonCardContent>
    </IonPage>
  );
};

export default NotificationsPage;
